// Copyright ©️ 2026 eVolve MEP, LLC

import { type ReactNode, useEffect, useMemo } from 'react';

import {
  type WorkOrderPaginated,
  useEvolvePaginatedGet,
  type EvolveApiParams,
  type WithRequired,
} from '@evolvemep/api-ts';
import { getDefaultZIndex, Flex, Text } from '@mantine/core';
import { useUncontrolled } from '@mantine/hooks';

import {
  SearchableSelect,
  type SearchableSelectProps,
} from 'components/Mantine/TypeSafeSelect/PaginatedSelectors/SearchableSelect';
import type { TypeSafeRenderOption } from 'components/Mantine/TypeSafeSelect/types';
import { getDisplayNameWO } from 'modules/Shop/WorkOrders/WorkOrderDetail/common';
import {
  NewWorkOrderModal,
  type NewWorkOrderModalProps,
} from 'modules/Shop/WorkOrders/WorkOrdersPage/NewWorkOrderModal';

type SearchableWorkOrder = WithRequired<Partial<WorkOrderPaginated>, 'workOrderId' | 'name' | 'identifier'>;

type BaseProps<T extends SearchableWorkOrder> = {
  value?: T | null;
  defaultValue?: T | null;
  onChange: (workOrder: T | null) => void;
  filters?: EvolveApiParams<'GET_PAGINATED', 'shop/workOrder'>;
  error?: ReactNode;
} & Pick<NewWorkOrderModalProps, 'defaultProject' | 'disableProjectSelection'>;

type Props<T extends SearchableWorkOrder> = BaseProps<T> &
  Omit<
    SearchableSelectProps<'shop/workOrder', WorkOrderPaginated, 'workOrderId'>,
    'idKey' | 'paginatedGet' | 'getItemLabel' | keyof BaseProps<T>
  >;

const ItemRenderer: TypeSafeRenderOption<string, { original: WorkOrderPaginated }> = (opts) => {
  const { name, identifier } = opts.option.original;
  return (
    <Flex align="center" gap="xs">
      <Text lineClamp={1}>{name}</Text>
      <Text opacity={0.6} style={{ textWrap: 'nowrap' }}>
        {identifier}
      </Text>
    </Flex>
  );
};

/**
 * A Searchable Select for choosing a Work Order to add Fabrication elements to.
 */
export const WorkOrderSearchableSelect = <T extends SearchableWorkOrder>({
  value,
  defaultValue,
  defaultProject,
  disableProjectSelection,
  onChange,
  filters: params,
  ...rest
}: Props<T>) => {
  const [__value, __onChange] = useUncontrolled({
    value,
    defaultValue,
    onChange,
  });

  const defaultOpts = useMemo(() => ({ defaultConfig: { params } }), [params]);
  const { setDefaultOpts: setDefaultOptsWorkOrders, ...workOrdersPaginatedGet } = useEvolvePaginatedGet(
    'shop/workOrder',
    defaultOpts,
  );
  useEffect(() => {
    setDefaultOptsWorkOrders(defaultOpts);
  }, [defaultOpts, setDefaultOptsWorkOrders]);

  return (
    <SearchableSelect<'shop/workOrder', WorkOrderPaginated, 'workOrderId'>
      label="Work Order"
      autoDropdownWidth
      createLabel="Create new work order"
      creatable
      creationModal={(defaultName, props) => (
        <NewWorkOrderModal
          {...props}
          defaultName={defaultName}
          defaultProject={defaultProject}
          disableProjectSelection={disableProjectSelection}
          onWorkOrderCreated={(v) => __onChange(v as T)}
          modalProps={{
            // The SearchableSelect is rendered inside of a popover,
            // so this Modal needs to render on top of it
            zIndex: getDefaultZIndex('popover') + 1,
          }}
        />
      )}
      renderOption={ItemRenderer}
      getItemLabel={getDisplayNameWO}
      {...rest}
      idKey="workOrderId"
      paginatedGet={workOrdersPaginatedGet}
      // Safe, because the renderers only ever use the fields on SearchableWorkOrder
      value={__value as WorkOrderPaginated | null}
      onChange={(v) => __onChange(v as T | null)}
    />
  );
};
